import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NetworkCanvas } from "@/components/NetworkCanvas";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeSlash } from "@phosphor-icons/react";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#08090F] px-4 py-3 text-white placeholder:text-[#475569] focus:border-[#2DD4BF]/50 focus:outline-none focus:ring-1 focus:ring-[#2DD4BF]/30 transition-colors";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="relative min-h-screen grid lg:grid-cols-2 bg-[#030305] text-white">
      <div className="relative hidden lg:block overflow-hidden border-r border-white/10">
        <NetworkCanvas className="absolute inset-0 h-full w-full" density={70} />
        <div className="absolute inset-0 qs-grid opacity-[0.12]" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center h-8 w-8 rounded-md border border-[#2DD4BF]/40 bg-[#2DD4BF]/10">
              <span className="h-3 w-3 rounded-sm bg-[#2DD4BF] rotate-45" />
            </span>
            <span className="font-black text-white">Quantum<span className="text-[#2DD4BF]">Scalp</span></span>
          </Link>
          <div>
            <h2 className="text-3xl font-black leading-tight max-w-sm">Understand. Verify. Then decide.</h2>
            <p className="mt-4 text-sm text-[#94A3B8] max-w-sm">
              Access the Q-Core platform. Trading involves risk — we prioritize transparency over hype.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-white mb-8 transition-colors">
            <ArrowLeft size={15} /> Back to site
          </Link>
          <h1 className="text-2xl font-black">{title}</h1>
          <p className="mt-1.5 text-sm text-[#94A3B8]">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [code, setCode] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password, mfa ? code : null);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      const dest = res.user.role === "admin" ? "/admin" : location.state?.from || "/dashboard";
      navigate(dest, { replace: true });
    } else if (res.mfa) {
      setMfa(true);
      toast.info("Enter your authenticator code.");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your Q-Core dashboard.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Email</label>
          <input data-testid="login-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </div>
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Password</label>
          <div className="relative">
            <input data-testid="login-password" type={show ? "text" : "password"} className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
              {show ? <EyeSlash size={17} /> : <Eye size={17} />}
            </button>
          </div>
        </div>
        {mfa && (
          <div>
            <label className="block text-sm text-[#cbd5e1] mb-1.5">Authenticator / Backup Code</label>
            <input data-testid="login-2fa-code" className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus />
          </div>
        )}
        <button type="submit" disabled={loading} data-testid="login-submit" className="w-full px-6 py-3.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors disabled:opacity-60">
          {loading ? "Signing in…" : mfa ? "Verify & Sign In" : "Sign In"}
        </button>
      </form>
      <p className="mt-6 text-sm text-[#94A3B8] text-center">
        New here?{" "}
        <Link to="/register" className="text-[#2DD4BF] hover:text-white transition-colors">Create an account</Link>
      </p>
      <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-1">Demo Member</p>
        <p className="font-mono text-xs text-[#94A3B8]">member@quantumscalp.ai · DemoMember2026!</p>
      </div>
    </AuthShell>
  );
}
